import { useState } from 'react';
import { isYouTubeId } from '@affily/shared/media';

export const youtubeThumbnail = (id) => `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;

/**
 * A YouTube video that is one thumbnail image until someone presses play. A
 * page of embeds would otherwise load YouTube's player, and its cookies, once
 * per video before anyone has asked to watch anything.
 *
 * The embed URL is built from the id alone, and the id is checked again here,
 * so nothing stored can point this iframe anywhere but YouTube.
 */
export function YouTubeEmbed({ id, title }) {
  const [playing, setPlaying] = useState(false);
  if (!isYouTubeId(id)) return null;
  const label = title || 'YouTube video';

  if (playing) {
    return (
      <iframe
        src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`}
        title={label}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        // The site sends Referrer-Policy: no-referrer; YouTube's player wants
        // to know which site it is embedded on, so this one iframe sends it.
        referrerPolicy="strict-origin-when-cross-origin"
        allowFullScreen
        className="size-full border-0"
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setPlaying(true)}
      aria-label={`Play: ${label}`}
      className="group relative block size-full bg-black"
    >
      <img
        src={youtubeThumbnail(id)}
        alt=""
        loading="lazy"
        className="size-full object-cover opacity-90 transition-opacity group-hover:opacity-100"
      />
      <span aria-hidden="true" className="absolute inset-0 flex items-center justify-center">
        <span className="flex h-11 w-14 items-center justify-center rounded-xl bg-black/75 text-white transition-transform group-hover:scale-105">
          <svg viewBox="0 0 24 24" className="size-6 fill-current">
            <path d="M8 5v14l11-7z" />
          </svg>
        </span>
      </span>
    </button>
  );
}

/** A small still of any gallery item, for the editor's list. */
export function GalleryThumb({ item, className = '' }) {
  const cls = `size-full object-cover ${className}`;
  if (item.type === 'youtube') {
    return isYouTubeId(item.youtubeId) ? (
      <img src={youtubeThumbnail(item.youtubeId)} alt="" className={cls} />
    ) : null;
  }
  if (item.type === 'video') return <video src={item.url} preload="metadata" muted className={cls} />;
  return <img src={item.url} alt="" className={cls} />;
}

function GalleryMedia({ item }) {
  if (item.type === 'youtube') return <YouTubeEmbed id={item.youtubeId} title={item.caption} />;
  if (item.type === 'video') {
    return (
      <video
        src={item.url}
        controls
        playsInline
        preload="metadata"
        className="size-full object-cover"
      />
    );
  }
  return (
    <a href={item.url} target="_blank" rel="noopener noreferrer" className="block size-full">
      <img src={item.url} alt={item.caption ?? ''} loading="lazy" className="size-full object-cover" />
    </a>
  );
}

/** A profile's photos, clips and YouTube videos. Photos open full size. */
export function Gallery({ items, className = '' }) {
  if (!items?.length) return null;
  return (
    <div className={`grid grid-cols-2 gap-3 sm:grid-cols-3 ${className}`}>
      {items.map((item, i) => (
        <figure
          key={`${i}-${item.youtubeId ?? item.url}`}
          className="overflow-hidden rounded-lg border border-rule bg-paper-raised"
        >
          <div className="aspect-[4/3] bg-paper-sunken">
            <GalleryMedia item={item} />
          </div>
          {item.caption && (
            <figcaption className="px-2 py-1.5 text-xs text-ink-muted">{item.caption}</figcaption>
          )}
        </figure>
      ))}
    </div>
  );
}
